import moment from 'moment';
import React from "react";
import { useSelector } from 'react-redux';

const scaleMap = {
    million: 1_000_000,
    billion: 1_000_000_000
};

const universalSorter = (valueA, valueB, profile) => {
    const extractText = (val) => {
        if (React.isValidElement(val)) {
            val = val.props?.children ?? '';
        }
        if (Array.isArray(val)) {
            val = val.map(child => {
                if (React.isValidElement(child)) {
                    return child.props?.children ?? '';
                }
                return child;
            }).join(' ');
        }
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (profile?.dateFormate && moment(trimmed, profile.dateFormate, true).isValid()) {
                return moment(trimmed, profile.dateFormate, true).toDate().getTime();
            }
            const scaleMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*(million|billion)/i);
            if (scaleMatch) {
                const number = parseFloat(scaleMatch[1]);
                const unit = scaleMatch[2].toLowerCase();
                return number * scaleMap[unit];
            }
            const cleaned = trimmed
                .replace(/^[^\d-]+/, '')
                .replace(/,/g, '')
                .replace(/\s+/g, '');
            const numberMatch = cleaned.match(/^(\d+(\.\d+)?)/);
            if (numberMatch) {
                return parseFloat(numberMatch[0]);
            }
            return trimmed.toLowerCase();
        }
        return '';
    };
    const a = extractText(valueA);
    const b = extractText(valueB);
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true });
};
export const generateDynamicColumns = (checkboxValues, ViewDetailPage, sortedInfo, defaultCurrency) => {
    let columns = [];
    const localData = useSelector((state) => state.localRedux);
    const Role = localData.role
    const ProfileData = localData.profile
    const profile = ProfileData
    if (checkboxValues !== undefined) {
        columns = checkboxValues
            .filter((item) => item.isChecked)
            .map((item) => {
                const column = {
                    title: item.fieldType === "Currency" ? `${item.name} (${defaultCurrency?.currency})` : (item.fieldType === "Percentage" ? `${item.name} (%)` : item.name),
                    dataIndex: item.fieldKey,
                    key: item.fieldKey,
                    sortDirections: ['ascend', 'descend'],
                    sortOrder: sortedInfo?.column?.dataIndex === item.fieldKey ? sortedInfo?.order : null,
                };
                switch (item.FieldValue) {
                    case 'accountId':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (record?.LinkAccount) {
                                    ViewDetailPage(record.accountID, "ACCOUNT")
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const figcaption = item.accountId.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children : '';
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'reportingTo':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (record?.linkContact) {
                                    ViewDetailPage(record.contactIDRTO, "CONTACT")
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const children = item?.reportingTo?.props?.children || [];
                                const figcaption = children.find(child => child && child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a) || '';
                            const valueB = extractNameText(b) || '';
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'leadOwner':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (Role === "Administrator" || profile.userId === record.userId) {
                                    ViewDetailPage(record.userId, "USER")
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const figcaption = item.leadOwner.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'name':
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const figcaption = item.name.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    default:
                        column.sorter = (a, b) => {
                            const valueA = a[item.FieldValue]?.props?.children || '';
                            const valueB = b[item.FieldValue]?.props?.children || '';
                            return universalSorter(valueA, valueB, profile);
                        };
                        break;
                }
                return column;
            });
    }
    return columns;
};
export const generateUsersTableData = (exportData, checkboxValues, defaultCurrency) => {
    const localData = useSelector((state) => state.localRedux);
    const Role = localData.role
    const ProfileData = localData.profile
    const profile = ProfileData
    const usersTableData = [];
    if (exportData && exportData[0] !== "") {
        exportData.forEach((contact, key) => {
            const customFields = {};
            contact.customFieldValues?.forEach((attr) => {
                const fieldConfig = checkboxValues.find(
                    (field) => field.id === attr.fieldId
                );
                if (!fieldConfig) return;
                let displayValue = attr.value;
                if (fieldConfig.fieldType === "Currency") {
                    displayValue = `${defaultCurrency?.symbol} ${Number(attr.value || 0)?.toLocaleString()}`
                } else if (fieldConfig.fieldType === "Percentage") {
                    displayValue = `% ${attr.value ?? "0"}`;
                } else if (
                    (fieldConfig.fieldType === "Dropdown" || fieldConfig.fieldType === 'Multi-Dropdown')
                ) {
                    displayValue = attr.option;
                }
                customFields[fieldConfig.fieldKey] = (
                    <div className="table_list_clicks">
                        {displayValue}
                    </div>
                );
            });
            usersTableData.push({
                LinkAccount: contact.accountPermission,
                linkContact: contact.reportingToPermission,
                ContactDataForPermission: contact,
                SharingData: contact?.sharingPermission?.sharePermission,
                sharingid: contact.fromId,
                userId: contact.leadOwner,
                accountID: contact.accountId,
                contactIDRTO: contact.reportingTo ? contact.reportingTo : contact.contacts_id,
                key: contact.contacts_id,
                no: key + 1,
                name: (
                    <div className="user-info_image table_list_clicks">
                        {contact.contacts_id && (contact.contactProfile !== null && contact.contactProfile !== undefined && contact.contactProfile !== "" ?
                            < figure >
                                <img style={{ width: "30px", height: "30px" }} src={contact.contactProfile} alt="" />
                            </figure>
                            :
                            <span className='list_span_Tag ContactIcon'>
                                {contact.firstName && contact.firstName.slice(0, 1).toUpperCase()}
                            </span>)
                        }
                        {contact.contacts_id &&
                            <figcaption>
                                <div>
                                    {`${contact.firstName} ${contact.lastName}`}
                                </div>
                                <div className='entityemail'>
                                    {contact.email}
                                </div>
                            </figcaption>
                        }
                    </div >
                ),
                accountId: (<div className={`user-info_image ${contact.accountPermission && "ListNavigate"}`}>
                    {contact.accountId &&
                        (contact.account_profile !== null && contact.account_profile !== undefined && contact.account_profile !== "" ?
                            < figure >
                                <img style={{ width: "30px", height: "30px" }} src={contact.account_profile} alt="" />
                            </figure>
                            :
                            <span className='list_span_Tag AccountIcon'>
                                {contact?.account_name?.slice(0, 1).toUpperCase()}
                            </span>)
                    }
                    {contact.accountId &&
                        <figcaption>
                            <div>
                                {contact.account_name}
                            </div>
                            <div className='entityemail'>
                                {contact.account_number}
                            </div>
                        </figcaption>
                    }
                </div>),
                leadOwner: (<div className={`user-info_image ${(Role === "Administrator" || profile.userId === contact.leadOwner) && "ListNavigate"}`}>
                    {contact.leadOwner && (contact.userProfilePic !== null && contact.userProfilePic !== undefined && contact.userProfilePic !== "" ?
                        < figure >
                            <img style={{ width: "30px", height: "30px" }} src={contact.userProfilePic} alt="" />
                        </figure>
                        :
                        <span className='list_span_Tag UserIcon'>
                            {contact.userFirstName && contact.userFirstName.slice(0, 1).toUpperCase()}
                        </span>)
                    }
                    {contact.leadOwner &&
                        <figcaption>
                            <div>
                                {`${contact.userFirstName} ${contact.userLastName}`}
                            </div>
                            <div className='entityemail'>
                                {contact.contactOwner}
                            </div>
                        </figcaption>
                    }
                </div>),
                reportingTo: (<div className={`user-info_image ${contact.reportingToPermission && "ListNavigate"}`}>
                    {contact.reportingTo && (contact.reportingToProfilePic !== null && contact.reportingToProfilePic !== undefined && contact.reportingToProfilePic !== "" ?
                        < figure >
                            <img style={{ width: "30px", height: "30px" }} src={contact.reportingToProfilePic} alt="" />
                        </figure>
                        :
                        <span className='list_span_Tag ContactIcon'>
                            {contact?.reportingToFirstName?.slice(0, 1).toUpperCase()}
                        </span>
                    )}
                    {contact.reportingTo &&
                        <figcaption>
                            <div>
                                {`${contact.reportingToFirstName} ${contact.reportingToLastName}`}
                            </div>
                            <div className='entityemail'>
                                {contact.reportingToEmail}
                            </div>
                        </figcaption>
                    }
                </div>),
                seniorityLevelId: (
                    <div
                        className='table_list_clicks'
                    >{contact.seniorityLevel}
                    </div>
                ),
                contactSourceId: (<div className='table_list_clicks status-text LeadSource'>{contact.contactSourceName}</div>),
                title: (<div className='table_list_clicks'>{contact.titleName}</div>),
                designation: (<div className='table_list_clicks'>{contact.designation}</div>),
                phone: (<div className='table_list_clicks'>{contact.phone}</div>),
                fax: (<div className='table_list_clicks'>{contact.fax}</div>),
                mobile: (<div className='table_list_clicks'>{contact.mobile}</div>),
                description: (<div className='table_list_clicks'>{contact.description ? `${contact.description.substring(0, 50)}...` : contact.description}</div>),
                emailoptout: (<div className='table_list_clicks'>{contact.emailOtpOut === 0 ? "No" : contact.emailOtpOut === 1 && "Yes"}</div>),
                country: (<div className='table_list_clicks'>{contact.country}</div>),
                state: (<div className='table_list_clicks'>{contact.state}</div>),
                city: (<div className='table_list_clicks'>{contact.city}</div>),
                street: (<div className='table_list_clicks'>{contact.street}</div>),
                postcode: (<div className='table_list_clicks'>{contact.postcode}</div>),
                email: (<div className='table_list_clicks'>{contact.email}</div>),
                assitantContactNumber: (<div className='table_list_clicks'>{contact.assitantContactNumber}</div>),
                assitantName: (<div className='table_list_clicks'>{contact.assitantName}</div>),
                department: (<div className='table_list_clicks' >{contact.department}</div>),
                created_at: (<div className='table_list_clicks'>
                    {contact?.created_at}
                </div>),
                modified_at: (<div className='table_list_clicks'>
                    {contact?.modified_at}
                </div>),
                contact_score: (
                    <div className='table_list_clicks'>
                        {Number(contact?.contact_score)?.toFixed(2)}
                    </div>
                ),
                linkedinUrl: (
                    <div className='table_list_clicks'>
                        {contact?.linkedinUrl}
                    </div>
                ),
                ...customFields,
            })
        })
    }
    return usersTableData;
};
